/**
 * The API client (ADR 0007, 0034): one function per endpoint of `@ab/api-contract`, on the page's own origin
 * (`/api/...`: the dev server proxies it to `pnpm api`; in production CloudFront routes it to the API). Every answer is
 * parsed by the contract's schema; its `serverNow` goes to the shell's `ServerTime` (the only way the client learns
 * the time). What a caller gets back when it is not the answer:
 *
 * - a refusal, as its code (`ui.server.<code>`);
 * - `offline` ("No connection."): no answer came back, a gateway in front of the API said it could not reach it
 *   (502, 503, 504), or the answer is not the contract's;
 * - `error` ("Something went wrong."): the API failed on the request (another 5xx without the contract's body).
 *
 * The caller shows it and the player tries again with a tap; nothing is retried or simulated here.
 *
 * **Unauthorized, in one place:** any endpoint's `unauthorized` forgets the token here and tells `onUnauthorized`'s
 * listeners (the account store, which signs out: the shell then shows the login over whatever is on screen).
 *
 * The session token is the one piece of account state on the device (`localStorage` `ab.session`, ADR 0034 D2). A log
 * out that got no answer still forgets it, and keeps it under `ab.session.revoke` only to delete its session on the
 * server with the next request (then the key goes).
 */
import {
  type AbandonResponse,
  type ClaimResponse,
  type CurrentRunResponse,
  type FloorResponse,
  type FloorSubmit,
  type JobResponse,
  type MailClaimResponse,
  type MailListResponse,
  type MailResponse,
  type MeResponse,
  type RunResponse,
  type SessionResponse,
  type ShownError,
  type SkipResponse,
  type StorageStack,
  type TicketCounts,
  abandonResponseSchema,
  changePasswordResponseSchema,
  claimResponseSchema,
  currentRunResponseSchema,
  errorResponseSchema,
  floorResponseSchema,
  jobResponseSchema,
  logoutResponseSchema,
  mailClaimResponseSchema,
  mailListResponseSchema,
  mailResponseSchema,
  meResponseSchema,
  runResponseSchema,
  serverNowSchema,
  sessionResponseSchema,
  skipResponseSchema,
} from '@ab/api-contract';
import type { z } from 'zod';
import type { ServerTime } from './clock.ts';

/** A refusal keeps the server's `requestId` (ADR 0037 D5), so a report can name the request; nothing shows it yet. */
export type ApiResult<T> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: ShownError; readonly requestId?: string };

const tokenKey = 'ab.session';
const revokeKey = 'ab.session.revoke';

const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string | null): void => {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Not kept (a private window): the page stays logged in until it is closed.
  }
};

/** The saved session token (null: none, or storage unavailable). */
export const savedToken = (): string | null => read(tokenKey);

/** Statuses of a gateway in front of the API that could not reach it: no connection, as far as the player goes. */
const gateway = new Set([502, 503, 504]);

export type Api = {
  token(): string | null;
  /** Forgets the token on this device. */
  forget(): void;
  /** Called whenever any request is answered `unauthorized` (the token is already forgotten then). */
  onUnauthorized(listener: () => void): () => void;
  /** A new account with an invite code (ADR 0038 D10) and its time zone (slice 8h, ADR 0042). */
  createAccount(name: string, password: string, inviteCode: string, timeZone: string): Promise<ApiResult<SessionResponse>>;
  logIn(name: string, password: string): Promise<ApiResult<SessionResponse>>;
  /** Deletes the session on the server and forgets the token; with no answer, the session is deleted with the next request. */
  logOut(): Promise<ApiResult<true>>;
  me(): Promise<ApiResult<MeResponse>>;
  /** `PUT /api/me/password`: the current password and a new one (ADR 0038 D8: after a temporary one). */
  changePassword(current: string, next: string): Promise<ApiResult<true>>;
  /** `POST /api/runs`: with `bag`, the stacks the packing panel chose (slice 8f; `bad_bag` when the server refuses them). */
  startRun(dungeon: string, bag?: readonly StorageStack[]): Promise<ApiResult<RunResponse>>;
  submitFloor(runId: string, floor: number, body: FloorSubmit): Promise<ApiResult<FloorResponse>>;
  currentRun(): Promise<ApiResult<CurrentRunResponse>>;
  abandonRun(runId: string): Promise<ApiResult<AbandonResponse>>;
  hatch(eggId: string, slot: string, idempotencyKey: string): Promise<ApiResult<JobResponse>>;
  claim(jobId: string, idempotencyKey: string): Promise<ApiResult<ClaimResponse>>;
  /**
   * `POST /api/jobs/:id/skip` (slice 8c): finishes an incubating job now for skip tickets: `plan`, the tickets the
   * player confirmed (slice 8h, ADR 0042: spent exactly; `bad_plan` or `not_enough_tickets`, nothing spent), or `auto`
   * (sent as `{auto: true}`: the server's choice at its own time, the fix round).
   */
  skip(jobId: string, idempotencyKey: string, plan: TicketCounts | 'auto'): Promise<ApiResult<SkipResponse>>;
  /** The mailbox (slice 8c, ADR 0039): the list, a mail opened, a mail claimed. */
  mail(): Promise<ApiResult<MailListResponse>>;
  readMail(mailId: string): Promise<ApiResult<MailResponse>>;
  claimMail(mailId: string, idempotencyKey: string): Promise<ApiResult<MailClaimResponse>>;
};

export const createApi = (time: ServerTime, base = '/api'): Api => {
  let token = savedToken();
  const unauthorized = new Set<() => void>();
  const setToken = (next: string | null): void => {
    token = next;
    write(tokenKey, next);
  };
  /** A logged-out token whose session the server has not deleted yet: tried once before each request until it is gone. */
  let revoking: Promise<void> | null = null;
  const revokePending = (): Promise<void> => {
    const old = read(revokeKey);
    if (old === null) return Promise.resolve();
    revoking ??= (async () => {
      try {
        const res = await fetch(`${base}/sessions/current`, { method: 'DELETE', headers: { authorization: `Bearer ${old}` } });
        // Deleted, or already unknown to the server: done either way.
        if (res.ok || res.status === 401) write(revokeKey, null);
      } catch {
        // Still no connection: the next request tries again.
      } finally {
        revoking = null;
      }
    })();
    return revoking;
  };
  const request = async <S extends z.ZodType>(method: string, path: string, schema: S, body?: unknown): Promise<ApiResult<z.infer<S>>> => {
    await revokePending();
    const headers: Record<string, string> = {};
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (token !== null) headers['authorization'] = `Bearer ${token}`;
    let res: Response;
    try {
      res = await fetch(`${base}${path}`, { method, headers, ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
    } catch {
      return { ok: false, error: 'offline' };
    }
    let json: unknown = null;
    try {
      json = await res.json();
    } catch {
      // Not JSON: a server error's text, or a page a gateway answered with.
    }
    const now = serverNowSchema.safeParse(json);
    if (now.success) time.observe(now.data.serverNow);
    if (!res.ok) {
      const refused = errorResponseSchema.safeParse(json);
      if (!refused.success) return { ok: false, error: res.status >= 500 && !gateway.has(res.status) ? 'error' : 'offline' };
      if (refused.data.error === 'unauthorized') {
        setToken(null);
        for (const l of [...unauthorized]) l();
      }
      const { requestId } = refused.data;
      return requestId === undefined ? { ok: false, error: refused.data.error } : { ok: false, error: refused.data.error, requestId };
    }
    const parsed = schema.safeParse(json);
    return parsed.success ? { ok: true, value: parsed.data } : { ok: false, error: 'offline' };
  };
  const session = async (path: string, body: { readonly name: string; readonly password: string; readonly inviteCode?: string; readonly timeZone?: string }): Promise<ApiResult<SessionResponse>> => {
    const r = await request('POST', path, sessionResponseSchema, body);
    if (r.ok) setToken(r.value.token);
    return r;
  };
  return {
    token: () => token,
    forget() {
      setToken(null);
    },
    onUnauthorized(listener) {
      unauthorized.add(listener);
      return () => unauthorized.delete(listener);
    },
    createAccount: (name, password, inviteCode, timeZone) => session('/accounts', { name, password, inviteCode, timeZone }),
    logIn: (name, password) => session('/sessions', { name, password }),
    async logOut() {
      const old = token;
      const r = await request('DELETE', '/sessions/current', logoutResponseSchema);
      // The device forgets the token whatever the answer; one the server could not delete now is deleted later.
      setToken(null);
      if (!r.ok && r.error !== 'unauthorized' && old !== null) write(revokeKey, old);
      return r.ok ? { ok: true, value: true } : r;
    },
    me: () => request('GET', '/me', meResponseSchema),
    async changePassword(current, next) {
      const r = await request('PUT', '/me/password', changePasswordResponseSchema, { current, next });
      return r.ok ? { ok: true, value: true } : r;
    },
    startRun: (dungeon, bag) => request('POST', '/runs', runResponseSchema, bag === undefined ? { dungeon } : { dungeon, bag }),
    submitFloor: (runId, floor, body) => request('POST', `/runs/${runId}/floors/${floor}`, floorResponseSchema, body),
    currentRun: () => request('GET', '/runs/current', currentRunResponseSchema),
    abandonRun: (runId) => request('POST', `/runs/${runId}/abandon`, abandonResponseSchema),
    hatch: (eggId, slot, idempotencyKey) => request('POST', '/jobs/hatch', jobResponseSchema, { eggId, slot, idempotencyKey }),
    claim: (jobId, idempotencyKey) => request('POST', `/jobs/${jobId}/claim`, claimResponseSchema, { idempotencyKey }),
    skip: (jobId, idempotencyKey, plan) => request('POST', `/jobs/${jobId}/skip`, skipResponseSchema, plan === 'auto' ? { idempotencyKey, auto: true } : { idempotencyKey, plan }),
    mail: () => request('GET', '/mail', mailListResponseSchema),
    readMail: (mailId) => request('POST', `/mail/${mailId}/read`, mailResponseSchema, {}),
    claimMail: (mailId, idempotencyKey) => request('POST', `/mail/${mailId}/claim`, mailClaimResponseSchema, { idempotencyKey }),
  };
};
